import { useApolloClient } from "@apollo/client";
import { DataRoomKind } from "@decentriq/graphql/dist/types";
import { useMutation } from "@tanstack/react-query";
import { useApiCore } from "contexts";
import { mapMediaDataRoomErrorToSnackbar, useDataRoomSnackbar } from "hooks";
import { DataRoomTypeNames } from "models";

interface usePublishedDataRoomStopArgs {
  dataRoomId: string;
  driverAttestationHash: string | undefined;
  onSuccess?: () => void;
  onError?: () => void;
  onSettled?: () => void;
  dataRoomKind?: DataRoomKind;
  __typename: DataRoomTypeNames;
}

const usePublishedDataRoomStop = ({
  dataRoomId,
  driverAttestationHash,
  onSettled,
  onSuccess,
  onError,
  dataRoomKind,
  __typename,
}: usePublishedDataRoomStopArgs) => {
  const { enqueueSnackbar } = useDataRoomSnackbar();
  const { cache: apolloCache } = useApolloClient();
  const { sessionManager, client } = useApiCore();

  const { mutate: stopDataRoomMutation, isPending: isDataRoomStopLoading } =
    useMutation({
      mutationFn: async () => {
        // Handle MDCR v3 case
        if (
          __typename === DataRoomTypeNames.PublishedMediaInsightsDcr &&
          dataRoomKind === DataRoomKind.MediaV3
        ) {
          await client.stopDataRoom(dataRoomId);
          return dataRoomId;
        }
        const session = await sessionManager.get({ driverAttestationHash });
        await session.stopDataRoom(dataRoomId);
        return dataRoomId;
      },
      onError: (error) => {
        enqueueSnackbar(
          ...mapMediaDataRoomErrorToSnackbar(
            error,
            "Can't stop data clean room"
          )
        );
        onError?.();
      },
      onSettled: () => {
        onSettled?.();
      },
      onSuccess: () => {
        enqueueSnackbar("Data clean room was stopped");
        onSuccess?.();
        const currentTimestamp = new Date().toISOString();
        apolloCache.modify({
          fields: {
            deactivatedAt: () => currentTimestamp,
            isActive: () => false,
            isStopped: () => true,
            stoppedAt: () => currentTimestamp,
          },
          id: apolloCache.identify({
            __typename,
            id: dataRoomId,
          }),
        });
      },
    });

  return {
    isDataRoomStopLoading,
    stopDataRoomMutation,
  };
};

export default usePublishedDataRoomStop;
